import { z } from 'zod';
import { emailSchema } from './auth.js';
import { LEAD_STAGES } from './trials.js';

/**
 * Platform reports (ADR-0158, BR-262): the weekly report e-mail and page, the month-over-month
 * comparison on the overview, and the Excel exports of the operator console. Counts only:
 * operators never see a merchant's documents (ADR-0080, BR-157).
 */

/** Weekly report figures; "this week" is the last complete Sunday–Saturday week in Kuwait. */
export const weeklyReportFiguresSchema = z.object({
  merchantsNewThisWeek: z.number().int(),
  merchantsNewLastWeek: z.number().int(),
  merchantsActive: z.number().int(),
  merchantsWithoutSales: z.number().int(),
  trialsEnding7Days: z.number().int(),
  subscriptionsEnding30Days: z.number().int(),
  subscriptionsOverdue: z.number().int(),
  salesThisWeek: z.number().int(),
  salesLastWeek: z.number().int(),
  onlineOrdersThisWeek: z.number().int(),
  onlineOrdersLastWeek: z.number().int(),
});
export type WeeklyReportFigures = z.infer<typeof weeklyReportFiguresSchema>;

export const reportSettingsSchema = z.object({
  weeklyEnabled: z.boolean(),
  extraRecipients: z.array(z.string()),
  version: z.number().int(),
});
export type ReportSettings = z.infer<typeof reportSettingsSchema>;

export const MAX_REPORT_RECIPIENTS = 10;

export const saveReportSettingsSchema = z.object({
  weeklyEnabled: z.boolean(),
  extraRecipients: z
    .array(emailSchema)
    .max(MAX_REPORT_RECIPIENTS)
    .transform((list) => [...new Set(list)]),
  version: z.number().int().min(1),
});
export type SaveReportSettings = z.input<typeof saveReportSettingsSchema>;

export const REPORT_RUN_KINDS = ['scheduled', 'manual'] as const;
export const REPORT_RUN_STATUSES = ['sending', 'sent', 'failed'] as const;

export const reportRunSchema = z.object({
  kind: z.enum(REPORT_RUN_KINDS),
  weekStart: z.string(),
  status: z.enum(REPORT_RUN_STATUSES),
  recipients: z.number().int(),
  claimedAt: z.string(),
  finishedAt: z.string().nullable(),
  operatorName: z.string().nullable(),
});
export type ReportRun = z.infer<typeof reportRunSchema>;

export const weeklyReportSchema = z.object({
  /** Sunday (YYYY-MM-DD, Asia/Kuwait) on which the reported week starts. */
  weekStart: z.string(),
  from: z.string(),
  to: z.string(),
  figures: weeklyReportFiguresSchema,
  settings: reportSettingsSchema,
  /** SALES_NOTIFY_EMAIL first, then the operator's extra recipients. */
  recipients: z.array(z.string()),
  /** When the next scheduled e-mail is due (Sunday 08:00 Asia/Kuwait). */
  nextRunAt: z.string(),
  runs: z.array(reportRunSchema),
});
export type WeeklyReport = z.infer<typeof weeklyReportSchema>;

export const weeklyPreviewSchema = z.object({
  to: z.array(z.string()),
  subject: z.string(),
  text: z.string(),
});
export type WeeklyPreview = z.infer<typeof weeklyPreviewSchema>;

export const weeklySendResultSchema = z.object({
  sent: z.number().int(),
  failed: z.number().int(),
});
export type WeeklySendResult = z.infer<typeof weeklySendResultSchema>;

/** One figure now and in the previous period of the same length. */
export const periodPairSchema = z.object({
  current: z.number().int(),
  previous: z.number().int(),
});
export type PeriodPair = z.infer<typeof periodPairSchema>;

export const overviewCompareSchema = z.object({
  /** Merchant total now vs. 30 days ago. */
  merchantsTotal: periodPairSchema,
  merchantsNew30Days: periodPairSchema,
  sales24Hours: periodPairSchema,
  onlineOrders24Hours: periodPairSchema,
  sales30Days: periodPairSchema,
  onlineOrders30Days: periodPairSchema,
});
export type OverviewCompare = z.infer<typeof overviewCompareSchema>;

/** Rows an Excel export carries at most; the sheet says when more matched (BR-262). */
export const EXPORT_ROW_CAP = 5000;

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => v || undefined);

/** Language of the sheet's headers and notes. */
const exportLang = { lang: z.enum(['ar', 'en']).default('en') };

export const merchantsExportQuerySchema = z.object({
  ...exportLang,
  q: optionalText(100),
  status: z.enum(['active', 'suspended', 'closed']).optional(),
});
export type MerchantsExportQuery = z.input<typeof merchantsExportQuerySchema>;

export const leadsExportQuerySchema = z.object({
  ...exportLang,
  stage: z.enum(LEAD_STAGES).optional(),
});
export type LeadsExportQuery = z.input<typeof leadsExportQuerySchema>;

export const auditExportQuerySchema = z.object({
  ...exportLang,
  merchantId: z.uuid().optional(),
  action: optionalText(60),
});
export type AuditExportQuery = z.input<typeof auditExportQuerySchema>;

export const EXPORT_KINDS = ['merchants', 'leads', 'audit'] as const;
export type ExportKind = (typeof EXPORT_KINDS)[number];
